import { hasWritePermission } from '../utils/biblioteca-actions'
import { SelectedItemBiblioteca } from '@/modules/biblioteca-virtual/types/types'
import {
  DownloadBatchQuery,
  DownloadBatchQueryVariables
} from '@/core/types/graphql'
import { DOWNLOAD_BATCH } from '@/modules/biblioteca-virtual/data/download-batch'
import { useEffect, useState } from 'react'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { t } from '@lingui/core/macro'
import { downloadFiles } from '@/modules/biblioteca-virtual/utils/functions'
import { request } from '../../../core/utils/request'
import { useQueryClient } from '@tanstack/react-query'

export const useToolbarActions = (selectedRows: SelectedItemBiblioteca[]) => {
  const client = useQueryClient()
  const { snackbar } = useSnackbar()

  const [loading] = useState<boolean>(false)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)

  useEffect(() => {
    if (loading) {
      snackbar.show('info', t`Aguarde enquanto geramos o seu download`)
    }
  }, [loading, snackbar])

  useEffect(() => {
    if (!isDownloading) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDownloading])

  const handleDownload = async () => {
    try {
      const ids = selectedRows.map(it => it.data.id)
      const { downloadBatch } = await client.fetchQuery({
        queryKey: ['downloadBatch', ids],
        queryFn: async () =>
          await request<DownloadBatchQuery, DownloadBatchQueryVariables>(
            DOWNLOAD_BATCH,
            { variables: { ids } }
          )
      })
      const singleFile =
        selectedRows.length === 1 && !selectedRows[0].data.isPasta

      await snackbar.promise(
        downloadFiles(downloadBatch, singleFile, setIsDownloading),
        {
          loading: { message: t`Aguarde enquanto geramos o seu download` },
          success: { message: t`Download gerado com sucesso!` }
        }
      )
    } catch (e) {
      snackbar.show(
        'error',
        t`Houve um erro enquanto tentamos processar o seu download (${(e as Error).message})`
      )
    }
  }

  const arquivosSelecionados = selectedRows.filter(
    row => row.selected && !row.data.isPasta
  )
  const pastasSelecionadas = selectedRows.filter(
    row => row.selected && row.data.isPasta
  )

  const isSelected = selectedRows.filter(row => row.selected)
  const hasPastaPai = selectedRows.filter(
    row => row.selected && row.data.pastaPai !== null
  )
  const hasPermissionWrite = () => {
    const permissionWrite = selectedRows.filter(
      row => row.selected && hasWritePermission(row.data)
    )

    return isSelected.every(row =>
      permissionWrite.some(
        rowPermission => row.data.id === rowPermission.data.id
      )
    )
  }
  const disabledRemove = () => !hasPermissionWrite()

  const disabledMove = () => !hasPermissionWrite()

  const disabledConcederAcesso = () =>
    (pastasSelecionadas.length > 0 && arquivosSelecionados.length > 0) ||
    isSelected.length > 1 ||
    !hasPermissionWrite() ||
    hasPastaPai.length > 0

  const disabledAlterarProprietario = () =>
    (pastasSelecionadas.length > 0 && arquivosSelecionados.length > 0) ||
    isSelected.length > 1

  const disabledRenomear = () =>
    !(
      isSelected.length === 1 &&
      hasWritePermission(isSelected[0].data) &&
      isSelected[0].data.isPasta
    )

  return {
    arquivosSelecionados,
    pastasSelecionadas,
    isSelected,
    disabledRemove,
    disabledMove,
    disabledConcederAcesso,
    disabledAlterarProprietario,
    disabledRenomear,
    handleDownload
  }
}
